import { useState, useEffect } from 'react';
import { Form } from "react-bootstrap";
import './DatePicker.css'


export default function DatePicker(props) {
    const { label, onChange, compensate, moreInfo, testId, disabled } = props;

    // Helper function to parse initial date values from Redux data
    const parseInitialDate = () => {
        if (!compensate?.dob || typeof compensate.dob !== 'string') {
            return { day: '', month: '', year: '' };
        }
        
        const dateParts = compensate.dob.split('/');
        if (dateParts.length === 3) {
            return {
                day: dateParts[0],
                month: dateParts[1],
                year: dateParts[2]
            };
        }
        return { day: '', month: '', year: '' };
    };

    const [dateValues, setDateValues] = useState(parseInitialDate());

    // Helper function to get the number of days in a month
    const getDaysInMonth = (month, year) => {
        if (!month || !year) return 31; // Default to 31 if month/year not selected
        
        const monthNum = parseInt(month);
        const yearNum = parseInt(year);
        
        // Days in each month (non-leap year)
        const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
        
        // Check for leap year for February
        if (monthNum === 2) {
            const isLeapYear = (yearNum % 4 === 0 && yearNum % 100 !== 0) || (yearNum % 400 === 0);
            return isLeapYear ? 29 : 28;
        }
        
        return daysInMonth[monthNum - 1];
    };

    // Generate day options based on selected month and year
    const maxDays = getDaysInMonth(dateValues.month, dateValues.year);
    const dayOptions = Array.from({ length: maxDays }, (_, i) => i + 1);
    
    // Generate month options (1-12)
    const monthOptions = [
        { value: '01', label: 'January' },
        { value: '02', label: 'February' },
        { value: '03', label: 'March' },
        { value: '04', label: 'April' },
        { value: '05', label: 'May' },
        { value: '06', label: 'June' },
        { value: '07', label: 'July' },
        { value: '08', label: 'August' },
        { value: '09', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' }
    ];
    
    // Generate year options (only years up to 2007 - ensuring 18+ age requirement)
    const minAgeYear = 2007; // Fixed cutoff - anyone born after 2007 would be under 18 in 2025
    const yearOptions = Array.from({ length: minAgeYear - 1899 }, (_, i) => minAgeYear - i);

    const handleDateChange = (field, value) => {
        const newDateValues = { ...dateValues, [field]: value };
        
        // If month or year changes, validate the current day
        if (field === 'month' || field === 'year') {
            const maxDaysForNewDate = getDaysInMonth(
                field === 'month' ? value : newDateValues.month,
                field === 'year' ? value : newDateValues.year
            );
            
            // If current day is invalid for the new month/year, reset to the max valid day
            if (newDateValues.day && parseInt(newDateValues.day) > maxDaysForNewDate) {
                newDateValues.day = maxDaysForNewDate.toString().padStart(2, '0');
            }
        }
        
        setDateValues(newDateValues);
        
        // Only call onChange when we have a complete valid date or when clearing
        if (newDateValues.day && newDateValues.month && newDateValues.year) {
            const formattedDate = `${newDateValues.day.padStart(2, '0')}/${newDateValues.month}/${newDateValues.year}`;
            onChange(formattedDate);
        } else {
            // Only call onChange with empty string if we're actually clearing a previously set date
            if (dateValues.day || dateValues.month || dateValues.year) {
                onChange('');
            }
        }
    };

    useEffect(() => {
        // Initialize with existing value if available
        if (dateValues.day && dateValues.month && dateValues.year) {
            const formattedDate = `${dateValues.day.padStart(2, '0')}/${dateValues.month}/${dateValues.year}`;
            onChange(formattedDate);
        }
    }, []);

    return (
        <>
            <Form.Label>{label}</Form.Label>
            {moreInfo && <p className="date-picker-info">{moreInfo}</p>}
            <div className="date-picker-container">
                <select
                    className="date-picker-select"
                    value={dateValues.day}
                    onChange={(e) => handleDateChange('day', e.target.value)}
                    data-testid={testId ? `${testId}-day` : undefined}
                    disabled={disabled}
                >
                    <option value="" disabled hidden>Day</option>
                    {dayOptions.map(day => (
                        <option key={day} value={day.toString().padStart(2, '0')}>
                            {day}
                        </option>
                    ))}
                </select>

                <select
                    className="date-picker-select"
                    value={dateValues.month}
                    onChange={(e) => handleDateChange('month', e.target.value)}
                    data-testid={testId ? `${testId}-month` : undefined}
                    disabled={disabled}
                >
                    <option value="" disabled hidden>Month</option>
                    {monthOptions.map(month => (
                        <option key={month.value} value={month.value}>
                            {month.label}
                        </option>
                    ))}
                </select>

                <select
                    className="date-picker-select"
                    value={dateValues.year}
                    onChange={(e) => handleDateChange('year', e.target.value)}
                    data-testid={testId ? `${testId}-year` : undefined}
                    disabled={disabled}
                >
                    <option value="" disabled hidden>Year</option>
                    {yearOptions.map(year => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>
            </div>
        </>
    )
}
